import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from 'styled-components';
import dayjs from 'dayjs';

import ModalContainer from 'containers/ProductRegist/components/common/ModalContainer';
import { Settlements } from 'containers/SettlementRequest/graphQL/types';
import { InvoiceNode } from '../graphQL/types';
import Row from 'components/common/Row';
import CommonImage from 'components/common/CommonImage';
import { ICON_CLOSE, ICON_SHARE } from 'assets/icons';
import Column from 'components/common/Column';
import SettlementBadge from 'containers/SettlementRequest/components/SettlementBadge';
import Text from 'components/common/Text';
import BasicButton from 'components/Button/BasicButton';
import Divider from 'components/Divider';
import { SETTLEMENT_DETAIL_PATH_DEFAULT } from 'constants/path';

const SettlementItemBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  border-radius: 4px;
  background-color: #f4f4f5;

  color: var(--gray-gray-09, #1b1c1e);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px; /* 133.333% */
  letter-spacing: -0.4px;
`;

const InvoiceInformation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  width: 100%;
  border-radius: 12px;
  background-color: #f7f7f8;
`;

interface SettlementDetailModalIProps {
  isMobile: boolean;
  settlements: Settlements;
  selectedInvoice: InvoiceNode;
  handleCloseDetailModal: () => void;
}

const SettlementDetailModal: React.FC<SettlementDetailModalIProps> = props => {
  const { isMobile, settlements, selectedInvoice, handleCloseDetailModal } =
    props;

  const navigate = useNavigate();
  const [isShowSettlements, setIsShowSettlements] = useState<boolean>(false);

  const renderInvoiceItem = (
    title: string,
    value: string | React.ReactElement,
  ): JSX.Element => {
    return (
      <Row justifyContent="space-between" style={{ width: '100%' }}>
        <Text color="#46474C">{title}</Text>
        <Text color="#1B1C1E" weight={400}>
          {value}
        </Text>
      </Row>
    );
  };

  const renderSettlements = (): JSX.Element => {
    return (
      <React.Fragment>
        {settlements &&
          settlements.edges.map((settlement, settlementIdx) => {
            return (
              <Column align="flex-start" gap="24px">
                <Column align="flex-start" key={settlementIdx} gap="16px">
                  <SettlementItemBox key={settlementIdx}>
                    {settlement.node.title}
                  </SettlementItemBox>
                  <Column gap="12px">
                    {renderInvoiceItem(
                      '정산 기간',
                      <span style={{ color: '#70737C' }}>{`${dayjs(
                        settlement.node.startedDate,
                      ).format('YYYY년 MM월 DD일')} ~ ${dayjs(
                        settlement.node.endedDate,
                      ).format('YYYY년 MM월 DD일')}`}</span>,
                    )}
                    {renderInvoiceItem(
                      '수입',
                      <span style={{ color: '#70737C' }}>
                        {Number(
                          settlement.node.totalPayment.value,
                        ).toLocaleString()}
                        원
                      </span>,
                    )}
                    {renderInvoiceItem(
                      '플랫폼 수수료',
                      <span style={{ color: '#70737C' }}>
                        {Number(
                          settlement.node.totalFee.value,
                        ).toLocaleString()}
                        원
                      </span>,
                    )}
                  </Column>
                  <Row justifyContent="space-between" style={{ width: '100%' }}>
                    <Text color="#1b1c1e" size="16px" weight={600}>
                      지급 금액
                    </Text>
                    <Text color="#1b1c1e" size="16px" weight={600}>
                      {Number(
                        settlement.node.totalPayment.value,
                      ).toLocaleString()}
                      원
                    </Text>
                  </Row>
                  <BasicButton
                    content={
                      <Row gap="4px">
                        <Text color="#46474C">정산 내역 보기</Text>
                        <CommonImage
                          src={ICON_SHARE}
                          onClick={() => {}}
                          style={{ width: '16px' }}
                        />
                      </Row>
                    }
                    onClick={() =>
                      navigate(
                        `${SETTLEMENT_DETAIL_PATH_DEFAULT}/${settlement.node.id}`,
                      )
                    }
                    style={{ width: '100%', borderRadius: '8px' }}
                  />
                </Column>
                {settlements.edges.length !== settlementIdx - 1 && <Divider />}
              </Column>
            );
          })}
      </React.Fragment>
    );
  };

  return (
    <ModalContainer
      isMobile={isMobile}
      isBottomSheet={true}
      isAbled={true}
      onClose={handleCloseDetailModal}
      onClick={() => {}}
    >
      <Column style={{ padding: '0 20px 16px' }}>
        <Row
          justifyContent="flex-start"
          style={{ padding: '16px 0', width: '100%' }}
        >
          <CommonImage src={ICON_CLOSE} onClick={handleCloseDetailModal} />
        </Row>
        <Column align="flex-start" gap="24px" style={{ padding: '8px 0' }}>
          <Row gap="8px">
            <SettlementBadge status={selectedInvoice.status} />
            <Text color="#1b1c1e" size="20px">
              {selectedInvoice.title.slice(5, 18)}
            </Text>
          </Row>
          <Row
            justifyContent="flex-start"
            gap="8px"
            style={{ flexWrap: 'wrap' }}
          >
            {settlements &&
              settlements.edges.map((settlement, settlementIdx) => {
                return (
                  <SettlementItemBox key={settlementIdx}>
                    {settlement.node.title.slice(2, 16)}
                  </SettlementItemBox>
                );
              })}
          </Row>
          <InvoiceInformation>
            <Column gap="12px">
              {renderInvoiceItem(
                '요청일',
                dayjs(selectedInvoice.createdAt).format('YYYY년 MM월 DD일'),
              )}
              {renderInvoiceItem(
                '진행 시작일',
                selectedInvoice.startedAt !== null
                  ? dayjs(selectedInvoice.startedAt).format('YYYY년 MM월 DD일')
                  : '-',
              )}
              {renderInvoiceItem(
                '지급 완료일',
                selectedInvoice.completedAt !== null
                  ? dayjs(selectedInvoice.completedAt).format(
                      'YYYY년 MM월 DD일',
                    )
                  : '-',
              )}
              <Column gap="4px" align="flex-end">
                {renderInvoiceItem(
                  '차감금액',
                  `${Number(
                    selectedInvoice.paymentForReceivable.value,
                  ).toLocaleString()}원`,
                )}
                <Text color="#ff4242" size="12px" weight={400}>
                  사용취소나 주문취소에 의해 차감된 금액
                </Text>
              </Column>
            </Column>
            <Row justifyContent="space-between" style={{ width: '100%' }}>
              <Text color="#1b1c1e" size="16px" weight={600}>
                총 지급 금액
              </Text>
              <Text color="#1b1c1e" size="16px" weight={600}>
                {Number(selectedInvoice.totalPayment.value).toLocaleString()}원
              </Text>
            </Row>
          </InvoiceInformation>
          {isShowSettlements ? (
            <Divider />
          ) : (
            <BasicButton
              content={<Text color="#46474c">회차별 지급 내역 확인</Text>}
              style={{ padding: '12px 0', width: '100%', borderRadius: '8px' }}
              onClick={() => setIsShowSettlements(true)}
            />
          )}
          {isShowSettlements && renderSettlements()}
        </Column>
      </Column>
    </ModalContainer>
  );
};

export default SettlementDetailModal;
